import { useStore } from '@vromo/mobx-react/mobx-react';
import Button from '@vromo/ui/components/Button';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import Modal, { ModalActions, ModalContent, ModalHeader } from '@vromo/ui/components/Modal';
import { useModals } from '@vromo/ui/components/Modals';
import Title from '@vromo/ui/components/Title';
import { useToasts } from '@vromo/ui/components/Toasts';
import { RootStore } from 'dashboard/src/stores/RootStore';
import format from 'date-fns/format';
import sortBy from 'lodash/sortBy';
import { action } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    JobHistoryResponse,
    UploadHistoryItem,
    openPhotoInNewTab
} from '../../helpers/JobHistoryHelper';
import styles, { getPhotoStyles } from './photoHistoryModal.style';

const DATE_FORMAT = ` hh:mma DD MMM YYYY `;

interface Props {
    isOpen: boolean;
    selectedJob?: JobHistoryResponse;
    onClose: () => void;
}

const PhotoHistoryModal = observer<Props>(({ isOpen, selectedJob, onClose }) => {
    const { t } = useTranslation();
    const translate = (key: string, params?: any) => t(`jobHistory.${key}`, params);
    const {
        authStore: { graph, role }
    } = useStore<RootStore>('rootStore');
    const modals = useModals();
    const showToast = useToasts();

    const [photos, setPhotos] = useState<UploadHistoryItem[]>([]);
    const [selectedPhoto, setSelectedPhoto] = useState<UploadHistoryItem | null>(null);

    useEffect(() => {
        if (!isOpen) return;

        setSelectedPhoto(null);
        setPhotos(selectedJob?.uploads?.filter((it) => it.attachmentType === 'PHOTO') || []);
    }, [isOpen, selectedJob]);

    const selectPhoto = (id: number) => setSelectedPhoto(photos?.find((p) => id === p.id) || null);

    const removePhoto = (id: number) => {
        if (!photos?.length || !selectedJob?.uploads?.length) return;

        const filteredPhotos = photos.filter((p) => p.id !== id);
        selectedJob.uploads = filteredPhotos;
        setPhotos(filteredPhotos);
        setSelectedPhoto(null);
    };

    const deletePhoto = async () => {
        const photoId = selectedPhoto?.id;
        if (
            selectedJob?.id &&
            photoId &&
            (await modals.confirm(translate('deletePhoto'), translate('deletePhotoDescription')))
        ) {
            try {
                await graph.root
                    .graph()
                    .roles(role.id)
                    .jobs(selectedJob.id)
                    .uploads()
                    .delete({ _all_id__in: photoId });

                action(() => {
                    showToast(translate('deletePhotoSuccess'), 'info');
                    photoId && removePhoto(photoId);
                });
            } catch (e) {
                console.error(e);
                showToast(translate('deletePhotoError'), 'error');
            }
        }
    };

    const uploadDateText = selectedPhoto?.created
        ? translate('uploadDate', { date: format(selectedPhoto.created, DATE_FORMAT) })
        : translate('unknownUploadDate');

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose}>
            <ModalHeader>
                <Title>{translate('photoHistory')}</Title>
            </ModalHeader>
            <ModalContent>
                {selectedJob?.uploads?.length ? (
                    <div className={styles.container}>
                        <div className={styles.photoExplorer}>
                            {photos && photos.length > 0 && (
                                <div className={styles.photoContainer}>
                                    {sortBy(photos, (p) => p.created).map(({ id, url }) => (
                                        <img
                                            src={url || ''}
                                            key={id}
                                            onClick={() => selectPhoto(id!)}
                                            className={getPhotoStyles(id === selectedPhoto?.id)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className={styles.inspectorContainer}>
                            {!selectedPhoto ? (
                                <p>{translate('selectPhoto')}</p>
                            ) : (
                                <div className={styles.mainPhotoContainer}>
                                    <img src={selectedPhoto?.url || ''} className={styles.photo} />
                                    <h3>{uploadDateText}</h3>
                                    <div className={styles.buttonContainer}>
                                        <Button
                                            primary
                                            small
                                            onClick={() => openPhotoInNewTab(selectedPhoto.url)}>
                                            <FontAwesome
                                                icon="external-link-alt"
                                                className={styles.buttonIcon}
                                            />
                                            {translate('openInTab')}
                                        </Button>
                                        <Button destructive small onClick={deletePhoto}>
                                            <FontAwesome
                                                icon="trash-alt"
                                                className={styles.buttonIcon}
                                            />
                                            {translate('delete')}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className={styles.emptyContainer}>{translate('noPhotos')}</div>
                )}
            </ModalContent>
            <ModalActions>
                <Button onClick={onClose}>{translate('close')}</Button>
            </ModalActions>
        </Modal>
    );
});

export default memo(PhotoHistoryModal);
